import Immutable from "immutable";
import _ from "lodash";

import AppConstants from "../constants/AppConstants";
import SiteDesignStore from "@/stores/SiteDesignStore";
import DocumentStore from "@/document/DocumentStore";
import { COMMANDS } from "@/document/commands";
import UIActions from "@/actions/UIActions";
import UserActions from "@/actions/UserActions";
import ModelFactory from "@/models/ModelFactory";
import GroupStore from "@/stores/GroupStore";
import SelectionStore from "@/stores/SelectionStore";
import DataTypes from "@/constants/DataTypes";
import GroupActions from "@/actions/GroupActions";
import BlockUtils from "@/utils/BlockUtils";
import IDUtils from "@/utils/IDUtils";
import { DEFAULT_MERGE_PROPS } from "@/constants/SyncConstants";
import { LAYOUTS } from "@/constants/DesignConstants.mjs";
import ElementTypeMaps from "@/constants/ElementTypeMaps";
import {
  FORM_FIELD_DATA_TYPE_EMAIL,
  FORM_FIELD_DATA_TYPE_TEL,
  FORM_FIELD_DATA_TYPE_TEXT,
  FORM_FIELD_INPUT_HEIGHTS,
  FORM_FIELD_WIDTHS,
} from "@/constants/FormFieldConstants.mjs";

/**
 * @typedef {import("@/types/Selection.mjs").ElementInfo} ElementSelectionInfo
 */

/**
 * Returns a Custom Post Type Filter Object
 * @param {"cat" | "tag" | "tax"} filterType category || tag || custom taxonomy
 * @returns {Map} Immutable Map with props: type, val, include?, taxSlug?
 */
export const getWpCustomPostTypeFilter = (/** cat | tag | tax **/ filterType) =>
  Immutable.Map({
    type: filterType,
    val: "",
    ...(filterType !== AppConstants.WP_TAG && { include: 1 }),
    ...(filterType === AppConstants.WP_CPT_FILTER_TAXONOMY && { taxSlug: "" }),
  });

/**
 * Formats a property update object for state changes
 * @param {string} name - The name of the property being updated
 * @param {*} value - The new value for the property
 * @param {*} [origValue] - The original value before the update (optional)
 * @param {string} [action] - The action type associated with this update (optional)
 * @param {boolean} [addUndo=true] - Whether to add this change to the undo stack
 * @param {string} [source="propertyInput"] - The source of the property update
 * @returns {Object} Formatted update object containing the property changes
 */
export const formatPropUpdate = (
  name,
  value,
  origValue,
  action,
  addUndo = true,
  source = "propertyInput"
) => {
  const update = {
    name,
    value,
    addUndo,
    source,
  };
  if (origValue) {
    update.origValue = origValue;
  }
  if (action) {
    update.action = action;
  }
  return update;
};

// if locked simple is found, deselect it and remove from filteredElements
const filterElements = (selectedElements) => {
  const lockedSimple = selectedElements.find(
    (element) => element.type === "simple" && element.lock
  );
  if (lockedSimple) {
    const layout = selectedElements.first()?.layout || "desktop";
    UIActions.selectElement({
      id: lockedSimple.id,
      type: lockedSimple.type,
      layout,
      blockId: lockedSimple.blockId,
      addTo: true,
      from: "convertToButton",
    });
    return selectedElements.filter((element) => element.id !== lockedSimple.id);
  }
  return selectedElements;
};

/**
 * Whether a text element's data is flagged as a legacy text form field.
 * @param {Immutable.Map} [textData]
 * @returns {boolean}
 */
const hasFormFieldFlag = (textData) => Boolean(textData?.getIn(["form", "isField"]));

/**
 * Checks if the selected elements can be converted to a button. A legacy text form field never
 * can; it converts to a form field instead.
 * @param {Immutable.List<ElementSelectionInfo> | Immutable.Map<string, ElementSelectionInfo>} selectedElements - List of selected elements
 * @param {Immutable.Map} block - The selected block's data.
 * @returns {boolean} True if selection contains exactly one text element and one simple element
 */
export const checkSelectionForButtonElements = (selectedElements, block) => {
  const isButtonShape =
    (selectedElements.size === 2 &&
      selectedElements.some((element) => element.type === DataTypes.TEXT) &&
      selectedElements.some((element) => element.type === DataTypes.SIMPLE) &&
      selectedElements.every((element) => !element.lock)) ||
    (selectedElements.size === 3 &&
      selectedElements.some((element) => element.type === DataTypes.TEXT) &&
      selectedElements.filter((element) => element.type === DataTypes.SIMPLE).size === 2 &&
      selectedElements.some((element) => element.type === DataTypes.TEXT && !element.lock) &&
      selectedElements.some((element) => element.type === DataTypes.SIMPLE && !element.lock));

  return (
    isButtonShape &&
    !selectedElements.some(
      (element) =>
        element.type === DataTypes.TEXT &&
        hasFormFieldFlag(block.getIn(IDUtils.getArray(element.id, 1)))
    )
  );
};

/**
 * Replaces the selected elements with one new element in their place: dissolves a group the
 * removal would leave with fewer than two elements, removes the selection (saving the undo point),
 * then adds the element at the selection's stacking position.
 * @param {Immutable.List<ElementSelectionInfo>} selectedElements
 * @param {(selectedElementsData: Immutable.List<Immutable.Map>, selectedCount: number) => Object} buildElement
 *   Builds the element to add from the remaining selection's data.
 * @returns {Object} The added element.
 */
const replaceSelectionWithElement = (selectedElements, buildElement) => {
  const selectedCount = selectedElements.size;
  let filteredElements = filterElements(selectedElements);
  let dissolved = false;

  const containerPath = filteredElements.first()?.containedBy;

  if (containerPath) {
    const blockData = SelectionStore.getBlock();
    const block = SiteDesignStore.getBlockContainer().getBlockEntity(blockData.get("id"));
    const elementContainer = block.getElementContainerEntity(containerPath);

    // dissolve if appropriate and reset selection. Prevents shifting and complexity elsewhere.
    if (elementContainer.shouldDissolve(filteredElements.size - 1)) {
      SiteDesignStore.dissolveGroup({ id: containerPath }, blockData.get("id"), false);
      const newSelectedElements = SelectionStore.getElements().filter((element) =>
        [DataTypes.SIMPLE, DataTypes.TEXT].includes(element.type)
      );
      filteredElements = filterElements(newSelectedElements);
      dissolved = true;
    }
  }

  const topElement = filteredElements.reduce((acc, element) => {
    const index = element.index ?? -1;
    return index > (acc?.index ?? -1) ? element : acc;
  }, null);

  const insertAt = topElement?.index ? topElement?.index - selectedCount + 1 : 0;
  const insertOutsideSelectedState = topElement?.stateId === undefined || topElement.stateId < 0;

  // A list, not the keyed Map the stage passes, so builders can spread its values.
  const selectedElementsData = filteredElements
    .toList()
    .map((element) => SiteDesignStore.getElementData(element.id));
  const element = buildElement(selectedElementsData, selectedCount);

  if (!dissolved && GroupStore.isFocused()) {
    GroupActions.holdFocus();
  }

  // @todo: RACE CONDITION - When element selection is removed, then it is added creates a timing gap
  // Consider using a pending state flag to determine with this is happening, and have that reflected when element selection is happening
  // Maybe reversing order of actions? create button, add element, then remove selected elements
  // Add safety checks in all components that are attempting to use get selected elements.
  // Prevent dissolve as it should have happened above.
  DocumentStore.dispatch({ type: COMMANDS.REMOVE_SELECTED_ELEMENTS, payload: true });

  DocumentStore.dispatch({
    type: COMMANDS.ADD_ELEMENT,
    payload: {
      element,
      isNew: true,
      prepare: false,
      addUndo: false,
      insertAt,
      insertOutsideSelectedState,
      containedBy: dissolved ? undefined : containerPath,
    },
  });

  return element;
};

/**
 * Builds a button from the selection's text and simple elements: the text becomes the label and
 * the simple gives the box.
 * @param {Immutable.List<Immutable.Map>} selectedElementsData
 * @param {number} selectedCount - How many elements were selected, before any were deselected.
 * @returns {Object} The button element data.
 */
const buildButtonFromSelection = (selectedElementsData, selectedCount) => {
  const textElement = selectedElementsData.find(
    (element) => element.get("type") === DataTypes.TEXT
  );
  const simpleElementWithLink = selectedElementsData.find(
    (element) =>
      element.get("type") === DataTypes.SIMPLE &&
      (element.get("link") !== undefined || element.get("pubs") !== undefined)
  );

  const simpleElement =
    simpleElementWithLink ||
    selectedElementsData.find((element) => element.get("type") === DataTypes.SIMPLE);
  const link =
    selectedCount === 2
      ? textElement.get("link", simpleElement.get("link"))
      : simpleElement.get("link");
  const pubs =
    selectedCount === 2
      ? textElement.get("pubs", simpleElement.get("pubs"))
      : simpleElement.get("pubs");
  let label = textElement.getIn(["content", "text"], "").replaceAll("<br>", "");
  if (label?.indexOf("<") > -1) label = "Click Here";

  const previousTextAndSimpleData = {
    label,
    link,
    pubs,
    visible: textElement.get("visible", "a"),
    desktop: {
      w: simpleElement.getIn(["desktop", "w"]),
      h: simpleElement.getIn(["desktop", "h"]),
      x: simpleElement.getIn(["desktop", "x"]),
      y: simpleElement.getIn(["desktop", "y"]),
    },
    mobile: {
      w: simpleElement.getIn(["mobile", "w"]),
      h: simpleElement.getIn(["mobile", "h"]),
      x: simpleElement.getIn(["mobile", "x"]),
      y: simpleElement.getIn(["mobile", "y"]),
    },
  };

  return _.merge(ModelFactory.getButton(), previousTextAndSimpleData);
};

/**
 * Creates a button element from selected text and simple elements then removes the original elements
 * @param {Immutable.List} selectedElements - List containing one text element and one simple element
 * @returns {void}
 */
export const createButtonFromSelection = (selectedElements) =>
  replaceSelectionWithElement(selectedElements, buildButtonFromSelection);

/**
 * Whether a text element's data is a legacy text form field. WordPress fields hide the legacy form
 * panel, so they do not count.
 * @param {Immutable.Map} [textData]
 * @returns {boolean}
 */
const isLegacyFormField = (textData) =>
  hasFormFieldFlag(textData) && !textData.getIn(["wp", "field"]);

/**
 * Checks if the selection is one legacy text form field (a text element with `form.isField`) that
 * can become a modern input, optionally with one plain text as its label and up to two simple
 * elements. All must be unlocked, out of text editing and share one group and canvas view; site
 * canvases cannot hold form fields.
 * @param {Immutable.List<ElementSelectionInfo> | Immutable.Map<string, ElementSelectionInfo>} selectedElements
 * @param {Immutable.Map} block - The selected block's data.
 * @returns {boolean}
 */
export const checkSelectionForFormFieldConversion = (selectedElements, block) => {
  if (!block || BlockUtils.hasSiteBlock(block)) return false;

  const texts = selectedElements.filter((element) => element.type === DataTypes.TEXT);
  const simpleCount = selectedElements.count((element) => element.type === DataTypes.SIMPLE);
  const scopes = new Set(
    selectedElements
      .toList()
      .map(({ containedBy, stateId }) => `${containedBy ?? ""}|${stateId ?? ""}`)
  );

  if (
    texts.size < 1 ||
    texts.size > 2 ||
    simpleCount > 2 ||
    texts.size + simpleCount !== selectedElements.size ||
    scopes.size !== 1 ||
    selectedElements.some((element) => element.lock || element.edit)
  ) {
    return false;
  }

  const textsData = texts.toList().map(({ id }) => block.getIn(IDUtils.getArray(id, 1)));
  const formFieldCount = textsData.count(hasFormFieldFlag);
  return formFieldCount === 1 && textsData.some(isLegacyFormField);
};

/**
 * Reads rich text as plain text the way the published legacy field does for its placeholder:
 * tags dropped (`<br>` included), entities decoded, trimmed.
 * @param {string} html
 * @returns {string}
 */
const toPlainText = (html) =>
  new DOMParser().parseFromString(html, "text/html").body.textContent.trim();

/** Words in a field's label that pick its input type. */
const DATA_TYPE_KEYWORDS = [
  [FORM_FIELD_DATA_TYPE_EMAIL, /\be-?mail/i],
  // Whole words only for the short ones, so "Tell us", "excellent" and "automobile" stay text.
  [FORM_FIELD_DATA_TYPE_TEL, /phone|\b(mobile|cell|tel)\b/i],
];

/**
 * Picks an input type from a field's label. A label naming more than one type stays text, since
 * the wrong type would reject valid answers.
 * @param {string} label
 * @returns {string} One of the `FORM_FIELD_DATA_TYPE_*` values.
 */
const getDataTypeFromLabel = (label) => {
  const matches = DATA_TYPE_KEYWORDS.filter(([, keyword]) => keyword.test(label));
  return matches.length === 1 ? matches[0][0] : FORM_FIELD_DATA_TYPE_TEXT;
};

/**
 * Builds a default input from a legacy text form field. Only the field's meaning carries over:
 * its text becomes the placeholder and its required flag the input's. In Subject and Reply To do
 * not carry over, since inputs have neither yet. The label is the selected plain text, else the
 * field's form label (its submission key), else the placeholder. It sits at the selection's
 * top-left corner in each layout, at the default size, and takes its look from the theme. Its
 * type is email or phone when the label says so.
 * @param {Immutable.List<Immutable.Map>} selectedElementsData
 * @returns {Object} The input element data.
 */
const buildFormFieldFromSelection = (selectedElementsData) => {
  const texts = selectedElementsData.filter((element) => element.get("type") === DataTypes.TEXT);
  const textElement = texts.find(isLegacyFormField);
  const labelElement = texts.find((element) => element !== textElement);
  const form = textElement.get("form");
  const placeholder = toPlainText(textElement.getIn(["content", "text"], ""));
  const input = ModelFactory.getFormFieldElement({ type: AppConstants.ELEMENT_TYPE_FORM_INPUT });
  const label =
    toPlainText(labelElement?.getIn(["content", "text"], "") ?? "") ||
    form.get("label") ||
    placeholder ||
    input.content.label;
  const topLeft = (layout, axis) =>
    Math.min(...selectedElementsData.map((element) => element.getIn([layout, axis], 0)));
  return {
    ...input,
    visible: textElement.get("visible", "a"),
    sync: [...DEFAULT_MERGE_PROPS[AppConstants.ELEMENT_TYPE_FORM_INPUT]],
    content: {
      dataType: getDataTypeFromLabel(label),
      label,
      name: label,
      ...(placeholder && { placeholder }),
      ...(form.get("req") && { required: 1 }),
    },
    ...Object.fromEntries(
      LAYOUTS.map((layout) => [
        layout,
        {
          ...input[layout],
          x: topLeft(layout, "x"),
          y: topLeft(layout, "y"),
          w: FORM_FIELD_WIDTHS[layout],
          h: FORM_FIELD_INPUT_HEIGHTS[layout],
        },
      ])
    ),
  };
};

/**
 * Replaces a legacy text form field, and the simples selected with it, with a default input, then
 * opens the input's properties section.
 * @param {Immutable.List<ElementSelectionInfo> | Immutable.Map<string, ElementSelectionInfo>} selectedElements
 * @param {string} source - Where the conversion was started from, reported to PostHog.
 * @returns {void}
 */
export const createFormFieldFromSelection = (selectedElements, source) => {
  const input = replaceSelectionWithElement(selectedElements, buildFormFieldFromSelection);
  UserActions.trackPostHogEvent("form_field_converted", {
    source,
    dataType: input.content.dataType,
  });
  UIActions.toggleElementPropertiesSection({
    name: ElementTypeMaps[AppConstants.ELEMENT_TYPE_FORM_INPUT],
  });
};
